/*
 * Zesto — product page purchase options (Shopify Subscriptions app block).
 *
 * The app renders the options as stacked radios; zesto-theme.css restyles them as a
 * row of pills. This script only adds presentation on top of the app's own radios:
 *  - short pill labels (full name kept for screen readers),
 *  - a one-line helper under the pills,
 * and fixes one thing the app gets wrong in this theme: its hidden `selling_plan`
 * input sits outside the product <form>, so the chosen plan was never submitted.
 * We associate it with the form via the `form` attribute, and disable it while empty
 * so a one-time purchase submits exactly what it did before.
 *
 * The app's radios (including the nested per-frequency radio, whose
 * data-selling-plan-id the app reads on every change) are left in the DOM untouched.
 *
 * Also: the mobile sticky add-to-cart summary, and the desktop gallery height fit.
 */
(() => {
  const BLOCK = '.shopify_subscriptions_app_block';

  function productFormId() {
    const button = document.querySelector('product-info button[name="add"][form]');
    return button ? button.getAttribute('form') : null;
  }

  function linkSellingPlanInput() {
    const formId = productFormId();
    document.querySelectorAll('.selected-selling-plan-id').forEach((input) => {
      if (formId) input.setAttribute('form', formId);
      input.disabled = !input.value;
    });
  }

  function shortenLabels(block) {
    block.querySelectorAll('.shopify_subscriptions_app_block_label > label').forEach((label) => {
      if (label.dataset.zestoPill) return;
      const textNodes = [...label.childNodes].filter((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
      const full = textNodes.map((n) => n.textContent.trim()).join(' ');
      if (!full) return;
      textNodes.forEach((n) => n.remove());
      const short = document.createElement('span');
      short.setAttribute('aria-hidden', 'true');
      short.textContent = full.split(/\s+/)[0];
      const srOnly = document.createElement('span');
      srOnly.className = 'sr-only';
      srOnly.textContent = full;
      label.append(short, srOnly);
      label.title = full;
      label.dataset.zestoPill = 'true';
    });
  }

  // The Subscriptions app prints each plan's price as JSON next to its radio; the discount % is
  // derived from it, so the page only ever advertises what the plan really charges.
  function money(json) {
    if (!json) return null;
    try {
      const data = JSON.parse(json.textContent);
      return { text: String(data.price || '').replace(/\s*[A-Z]{3}$/, ''), value: parseFloat(String(data.price || '').replace(/[^0-9.]/g, '')) };
    } catch (e) {
      return null;
    }
  }

  function planInfo(plan) {
    if (!plan) return null;
    const base = money(document.getElementById(`subscriptions_money_with_currency_one_time_purchase_${plan.dataset.variantId || plan.closest(BLOCK)?.dataset.variantId}`))
      || money(plan.closest(BLOCK)?.querySelector('script[id^="subscriptions_money_with_currency_one_time_purchase"]'));
    const own = money(document.getElementById(`subscriptions_money_with_currency_${plan.dataset.sellingPlanGroupId}_${plan.dataset.sellingPlanId}`));
    if (!base || !own || !base.value) return { pct: 0 };
    return { pct: Math.max(0, Math.round((1 - own.value / base.value) * 100)), price: own.text };
  }

  // "-20%" tag on each frequency pill that really has a discount.
  function tagPills(block) {
    block.querySelectorAll('.shopify_subscriptions_app_block_label').forEach((group) => {
      const plan = group.querySelector('input[data-radio-type="selling_plan"]');
      const pill = group.querySelector(':scope > label');
      if (!plan || !pill) return;
      const info = planInfo(plan);
      const existing = pill.querySelector('.zesto-po-tag');
      if (info && info.pct > 0) {
        if (existing) existing.textContent = `-${info.pct}%`;
        else {
          const tag = document.createElement('b');
          tag.className = 'zesto-po-tag';
          tag.textContent = `-${info.pct}%`;
          pill.append(tag);
        }
      } else if (existing) existing.remove();
    });
  }

  // Main Add to Cart label follows the chosen plan: "Subscribe & save 20%".
  function updateCartLabel(info) {
    document.querySelectorAll('.zesto-main-product-section .product-form__submit .btn-text').forEach((label) => {
      if (!label.dataset.zestoLabel) label.dataset.zestoLabel = label.textContent.trim();
      label.textContent = info && info.pct > 0 ? `Subscribe & save ${info.pct}%` : label.dataset.zestoLabel;
    });
  }

  // Main price line: discounted price with the one-time price struck through while a plan is chosen.
  function updatePrice(plan, info) {
    document.querySelectorAll('.zesto-main-product-section .zesto-price-row .price').forEach((price) => {
      if (price.dataset.zestoOriginal === undefined) price.dataset.zestoOriginal = price.innerHTML;
      if (plan && info && info.pct > 0) {
        const base = money(document.getElementById(`subscriptions_money_with_currency_one_time_purchase_${plan.dataset.variantId || plan.closest(BLOCK)?.dataset.variantId}`));
        price.innerHTML = `<span class="zesto-po-now">${info.price}</span> <s class="zesto-po-was">${base ? base.text : ''}</s>`;
      } else {
        price.innerHTML = price.dataset.zestoOriginal;
      }
    });
  }

  // Floating Add to Cart card: a plan picker that mirrors the hero pills (choosing here clicks the same pill).
  function syncStickyPlan(block) {
    const host = document.querySelector('[data-zesto-sticky-plan]');
    if (!host) return;
    const groups = [...block.querySelectorAll('.shopify_subscriptions_app_block_label')];
    const items = groups.map((group) => {
      const pill = group.querySelector(':scope > label');
      const plan = group.querySelector('input[data-radio-type="selling_plan"]');
      const name = (pill.querySelector('[aria-hidden="true"]') || pill).textContent.trim().split(/\s+/)[0];
      let text = name;
      let pct = 0;
      let price = '';
      if (plan) {
        const info = planInfo(plan);
        if (info && info.pct > 0) { text = `${name} · -${info.pct}% · ${info.price}`; pct = info.pct; price = info.price; }
      } else {
        const base = money(block.querySelector('script[id^="subscriptions_money_with_currency_one_time_purchase"]'));
        if (base) { text = `${name} · ${base.text}`; price = base.text; }
      }
      return { pill, text, name, pct, price, checked: !!pill.querySelector(':scope > input:checked') };
    });
    if (items.length < 2) return;
    let select = host.querySelector('select');
    if (!select || select.options.length !== items.length) {
      host.textContent = '';
      select = document.createElement('select');
      select.setAttribute('aria-label', 'Purchase option');
      items.forEach((item, index) => select.add(new Option(item.text, String(index))));
      select.addEventListener('change', () => {
        const target = items[Number(select.value)];
        if (target) target.pill.click();
      });
      host.append(select);
    }
    items.forEach((item, index) => { select.options[index].text = item.text; });
    const checkedIndex = items.findIndex((item) => item.checked);
    if (checkedIndex > -1) select.value = String(checkedIndex);
    host.hidden = false;
    syncPlanPicker(host, select, items, checkedIndex);
  }

  // Phone: the plan select is replaced by a button that opens a bottom sheet with large, tappable options.
  let planSheet = null;
  function syncPlanPicker(host, select, items, checkedIndex) {
    let ui = host.querySelector('.zesto-plan-ui');
    if (!ui) {
      ui = document.createElement('div');
      ui.className = 'zesto-plan-ui';
      ui.innerHTML = '<button type="button" class="zesto-plan-btn" aria-haspopup="dialog"><span class="zesto-plan-btn__text"></span><span class="zesto-plan-btn__chev" aria-hidden="true"></span></button>';
      host.append(ui);
      planSheet = document.createElement('div');
      planSheet.className = 'zesto-plan-sheet';
      planSheet.hidden = true;
      planSheet.setAttribute('role', 'dialog');
      planSheet.setAttribute('aria-label', 'Choose how to buy');
      planSheet.innerHTML = '<div class="zesto-plan-sheet__backdrop"></div><div class="zesto-plan-sheet__panel"><div class="zesto-plan-sheet__head"><p class="zesto-plan-sheet__title">How would you like it?</p><button type="button" class="zesto-plan-sheet__close" aria-label="Close">&times;</button></div><div class="zesto-plan-sheet__list"></div></div>';
      document.body.append(planSheet);
      const close = () => { planSheet.hidden = true; };
      ui.querySelector('button').addEventListener('click', () => { planSheet.hidden = false; });
      planSheet.querySelector('.zesto-plan-sheet__backdrop').addEventListener('click', close);
      planSheet.querySelector('.zesto-plan-sheet__close').addEventListener('click', close);
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
      planSheet.addEventListener('click', (event) => {
        const opt = event.target.closest('[data-plan-index]');
        if (!opt) return;
        select.value = opt.dataset.planIndex;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close();
      });
    }
    const current = items[checkedIndex > -1 ? checkedIndex : 0];
    ui.querySelector('.zesto-plan-btn__text').textContent = current.pct > 0 ? `${current.name} · -${current.pct}%` : current.name;
    planSheet.querySelector('.zesto-plan-sheet__list').innerHTML = items.map((item, index) => `<button type="button" class="zesto-plan-opt${index === checkedIndex ? ' is-on' : ''}" data-plan-index="${index}" aria-pressed="${index === checkedIndex}"><span class="zesto-plan-opt__box" aria-hidden="true"></span><span class="zesto-plan-opt__name">${item.name}</span>${item.pct > 0 ? `<span class="zesto-plan-opt__tag">-${item.pct}%</span>` : ''}<span class="zesto-plan-opt__price">${item.price}</span></button>`).join('');
  }

  function helperText(block) {
    const plan = block.querySelector('input[data-radio-type="selling_plan"]:checked');
    if (!plan) return 'One-time purchase selected.';
    const frequency = plan.closest('label').textContent.trim().replace(/^Deliver\b/, 'Delivered');
    const info = planInfo(plan);
    if (info && info.pct > 0) return `${info.price} per bag · ${info.pct}% off your first 4 orders · Free shipping · Pause or cancel anytime`;
    return `${frequency} · auto-renews, skip or cancel anytime.`;
  }

  // Sticky add-to-cart bar (mobile): echo the chosen purchase option and quantity.
  // Its button submits the main product form, so it always sends exactly these values.
  function updateStickySummary() {
    const summary = document.querySelector('[data-zesto-sticky-summary]');
    if (!summary) return;
    const formId = productFormId();
    const form = formId && document.getElementById(formId);
    const qtyInput = document.querySelector(`input[name="quantity"][form="${formId}"]`) || (form && form.querySelector('input[name="quantity"]'));
    const checked = document.querySelector(`${BLOCK} .shopify_subscriptions_app_block_label > label.is-checked, ${BLOCK} .shopify_subscriptions_app_block_label > label:has(> input:checked)`);
    const option = checked ? (checked.querySelector('[aria-hidden="true"]') || checked).textContent.trim() : '';
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    summary.textContent = [option, `Qty ${qty}`].filter(Boolean).join(' · ');
  }

  function update() {
    linkSellingPlanInput();
    document.querySelectorAll(BLOCK).forEach((block) => {
      block.querySelectorAll('.shopify_subscriptions_app_block_label > label').forEach((label) => {
        label.classList.toggle('is-checked', !!label.querySelector(':scope > input:checked'));
      });
      tagPills(block);
      const helper = block.querySelector('.zesto-po-helper');
      if (helper) helper.textContent = helperText(block);
      const chosen = block.querySelector('input[data-radio-type="selling_plan"]:checked');
      const chosenInfo = planInfo(chosen);
      updateCartLabel(chosenInfo);
      updatePrice(chosen, chosenInfo);
      syncStickyPlan(block);
    });
    updateStickySummary();
  }

  function init() {
    const blocks = document.querySelectorAll(BLOCK);
    if (!blocks.length) return updateStickySummary();
    blocks.forEach((block) => {
      shortenLabels(block);
      const options = block.querySelector('.shopify_subscriptions_app_block_purchase_options');
      if (options && !block.querySelector('.zesto-po-helper')) {
        const helper = document.createElement('p');
        helper.className = 'zesto-po-helper';
        helper.setAttribute('aria-live', 'polite');
        options.after(helper);
      }
      block.classList.add('zesto-po-ready');
    });
    update();
  }

  // Bubble-phase listener: runs after the app's own per-radio handler has set the plan.
  document.addEventListener('change', (event) => {
    if (event.target.closest && event.target.closest(BLOCK)) update();
    else if (event.target.name === 'quantity') updateStickySummary();
  });
  // The quantity stepper buttons set the value without always firing `change`.
  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('quantity-selector, .quantity')) requestAnimationFrame(updateStickySummary);
  });
  document.addEventListener('input', (event) => {
    if (event.target.name === 'quantity') updateStickySummary();
  });

  // Desktop hero: the thumbnail gallery ends exactly level with the bottom of the
  // Add to Cart button (share, tabs etc. continue below it in the buy-box column).
  // Without JS the CSS fallback sizes it to the whole buy-box column.
  const DESKTOP = window.matchMedia('(min-width: 1024px)');
  function fitGallery() {
    const gallery = document.querySelector('.zesto-main-product-section .product--thumbnail > media-gallery');
    // Gallery ends level with the last collapsed accordion row (Shipping Delivery), else the Add to Cart button.
    const rows = document.querySelectorAll('.zesto-main-product-section product-info .product__accordion');
    const atc = rows.length ? rows[rows.length - 1] : document.querySelector('.zesto-main-product-section product-info .product-form__submit');
    if (!gallery) return;
    if (!DESKTOP.matches || !atc || !atc.offsetParent) {
      gallery.classList.remove('zesto-gallery-fit');
      gallery.style.removeProperty('--zesto-gallery-h');
      return;
    }
    // An opened row would drag the gallery down with it; keep the collapsed-state height.
    if ([...rows].some((row) => row.open) && gallery.classList.contains('zesto-gallery-fit')) return;
    // Layout offsets, not getBoundingClientRect: the buy-box slides in (translateY) on load,
    // and measuring mid-animation made the gallery start too tall, then shrink (layout shift).
    const layoutTop = (el) => { let y = 0; for (; el; el = el.offsetParent) y += el.offsetTop; return y; };
    const height = layoutTop(atc) + atc.offsetHeight - layoutTop(gallery);
    if (height < 320) return;
    gallery.style.setProperty('--zesto-gallery-h', `${height.toFixed(2)}px`);
    gallery.classList.add('zesto-gallery-fit');
  }
  function initGalleryFit() {
    const info = document.querySelector('.zesto-main-product-section product-info');
    fitGallery();
    if (info && window.ResizeObserver) new ResizeObserver(() => requestAnimationFrame(fitGallery)).observe(info);
    window.addEventListener('resize', fitGallery);
    window.addEventListener('load', fitGallery);
    // Re-measure once the theme's reveal animations have settled the button in place.
    if (info) ['transitionend', 'animationend'].forEach((type) => info.addEventListener(type, () => requestAnimationFrame(fitGallery)));
    [600, 1500, 3000].forEach((ms) => setTimeout(fitGallery, ms));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', initGalleryFit);
  } else {
    init();
    initGalleryFit();
  }
})();
